export enum DownloadStatus {
  Full = "Full",
  Partial = "Partial",
  Unknown = "Unknown",
}

export enum BookStatus {
  Ongoing = "Ongoing",
  Completed = "Completed",
  Hiatus = "Hiatus",
  Dropped = "Dropped",
  Unknown = "Unknown",
}

export interface BookRecord {
  id: number;
  source_id: string;
  title: string;
  authors: string[];
  description: string[] | null;
  genres: string[] | null;
  tags: string[] | null;
  url: string;
  first_chapter_url: string | null;
  status: BookStatus | null;
  latest_chapter_title: string | null;
  latest_chapter_url: string | null;
}

export interface Book extends BookRecord {
  download_status: DownloadStatus;
  needs_extension: boolean;
  resume_chapter_id: number | null;
  resume_chapter_title: string | null;
  is_downloading: boolean;
}

export interface Task {
  id: number;
  book_id: number;
  state: string;
  is_extension: boolean;
  downloaded_chapters: number;
  error_message: string | null;
  result: string | null;
  start_chapter: string | null;
  last_downloaded_chapter_title: string | null;
  last_downloaded_chapter_url: string | null;
  next_chapter_url: string | null;
  created_at: string;
  updated_at: string;
  username: string | null;
}

export interface PaginatedJobs {
  items: Task[];
  next_cursor: number | null;
}

export interface GetJobsOptions {
  cursor?: number;
  limit?: number;
  book_id?: number;
  source?: string;
}

export interface Chapter {
  id: number;
  book_id: number;
  title: string;
  url: string;
  file_path: string;
}

export enum Marker {
  SentenceStart = "SentenceStart",
  SentenceOverflow = "SentenceOverflow",
  ParagraphStart = "ParagraphStart",
  HeaderStart = "HeaderStart",
  HeaderEnd = "HeaderEnd",
}

export interface SplitUnit {
  text: string;
  marker: Marker;
}

export interface PaginatedChapters {
  items: Chapter[];
  next_cursor: number | null;
}

export interface GetChaptersOptions {
  cursor?: number;
  limit?: number;
  desc?: boolean;
}

export interface SearchResult {
  title: string;
  authors: string[];
  url: string;
  cover_url: string | null;
  latest_chapter_title: string | null;
  latest_chapter_url: string | null;
  source_id: string;
  in_library: boolean;
}

export interface SearchResponse {
  results: SearchResult[];
  failed_sources: string[];
}

export interface ChapterDetails extends Chapter {
  prev_id: number | null;
  next_id: number | null;
  structured: SplitUnit[];
}

export interface SourceListItem {
  meta: {
    source_id: string;
    base_url: string;
    needs_extension: boolean;
  };
}

export interface SourceConfig {
  meta: {
    source_id: string;
    base_url: string;
    needs_extension: boolean;
    is_spa?: boolean;
  };
  is_book_page: string;
  is_chapter_page: string;
  spa_loaded_indicators?: string[];
  book_page: {
    title: string;
    author: string;
    cover?: string;
    first_chapter: string;
    latest_chapter_url?: string;
    latest_chapter_title?: string;
    status?: string;
    genres?: string;
    tags?: string;
    description?: string;
  };
  chapter_page: {
    title: string;
    content: string;
    next_chapter: string;
  };
  search: {
    search_url: string;
    result_list: string;
    title: string;
    link: string;
    author?: string;
    cover?: string;
    status?: string;
    latest_chapter_title?: string;
  };
  status_mapping: Record<string, string>;
  sample_urls?: {
    search: string;
    book_info: string;
    chapter: string;
  };
}

export interface SourceInfo {
  id: string;
  config: SourceConfig;
  disabled: boolean;
}

export interface BookSearchResult {
  id: number;
  title: string;
  source_id: string;
}
